import { useRef } from "react";
import { format } from "date-fns";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Dropdown from "@/shared/components/ui/dropdown/dropdown";
import type { MarkerProps } from "@/map/types/map";
import { MARKER_STYLE } from "@/map/utils/mapConstants";

// Selected-alert ring color — blue-600, the app's map/timeline "active" accent
// (same value as the location pin color and `--timeline-cursor`). Distinct from
// every alert status color and from the white new-alert ring.
const SELECTED_RING_COLOR = "#2563eb";

export const MapMarker: React.FC<MarkerProps> = ({
  isActive = false,
  lat,
  lng,
  time,
  onClick,
  markerId,
  label,
  color = MARKER_STYLE.defaultColor,
  isLast = false,
  disableShadow = false,
  count,
  markerType,
  isReviewed = false,
  isSelected = false,
  zIndex,
  dimmed = false,
  onHover,
}) => {
  const { t } = useTranslation("map");
  const ref = useRef<HTMLButtonElement>(null);
  const isCluster = count && count > 1;
  // Location/machine pins (no markerType) carry a default name tag; alert/gps
  // points don't (there can be hundreds) — they keep the hover tooltip only.
  const showDefaultLabel = !isCluster && !markerType && !!label;

  // Determine size based on markerType
  let size: number;
  if (isCluster) {
    size = 40;
  } else if (isReviewed || isSelected) {
    size = 20;
  } else if (markerType === 'alert') {
    size = isLast ? 16 : 10; // Smaller for alerts
  } else if (markerType === 'gps') {
    size = isLast ? 16 : 10; // Bigger for GPS
  } else {
    size = isLast ? MARKER_STYLE.sizes.last : MARKER_STYLE.sizes.normal;
  }

  // Selection wins over the reviewed/new ring — a bold blue ring + blue glow so
  // the selected alert reads as distinct even among other highlighted markers.
  const shadow = isSelected
    ? `0 0 0 3px ${SELECTED_RING_COLOR}, 0 0 14px 6px rgba(37,99,235,0.9)`
    : isReviewed
      ? `0 0 0 4px rgba(255,255,255,0.8), 0 0 12px 6px ${color}`
      : (isLast && !disableShadow) ? `0 0 10px 16px ${color}` : "none";
  const formattedTime = time ? format(new Date(time), "HH:mm:ss") : null;

  // Determine background color
  let backgroundColor: string;
  if (isCluster) {
    backgroundColor = "#f97316"; // orange-500
  } else if (markerType === 'alert') {
    backgroundColor = color; // Use the alert's status color
  } else if (isActive) {
    backgroundColor = "#4ade80"; // green-400
  } else {
    backgroundColor = "#f87171"; // red-400
  }

  return (
    <>
      <button
        ref={ref}
        className={classNames(
          "absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full border-2 duration-200 hover:scale-125 flex items-center justify-center",
          // Gentle highlight pulse (slower than Tailwind's default animate-pulse).
          (isReviewed || isSelected) && "[animation:pulse_2.5s_ease-in-out_infinite]"
        )}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderColor: isSelected ? SELECTED_RING_COLOR : "white",
          boxShadow: shadow,
          backgroundColor: backgroundColor,
          zIndex: zIndex ?? (isReviewed || isSelected ? 10 : undefined),
          opacity: dimmed ? 0.5 : undefined,
          transition: "opacity 150ms ease",
        }}
        onClick={(e) => onClick(e, { markerId, lat, lng, time })}
        onMouseEnter={onHover ? () => onHover(markerId, true) : undefined}
        onMouseLeave={onHover ? () => onHover(markerId, false) : undefined}
      >
        {isCluster && (
          <span className="text-white text-xs font-bold">
            {count}
          </span>
        )}
        {showDefaultLabel && (
          <span className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-background/90 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-foreground shadow-sm backdrop-blur">
            {label}
          </span>
        )}
      </button>
      <Dropdown triggerType="hover" buttonRef={ref}>
        <div className="p-2 text-xs whitespace-nowrap">
          {isCluster ? (
            <>
              <div>{t("marker.cluster", { count: count ?? 0 })}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{t("marker.clusterHint")}</div>
            </>
          ) : (
            <>
              <div className="font-medium">{label ?? markerId}</div>
              {formattedTime && (
                <div className="text-muted-foreground">
                  {t("marker.time", { time: formattedTime })}
                </div>
              )}
            </>
          )}
        </div>
      </Dropdown>
    </>
  );
};
